import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import httpRequest from '../utils/request';
import { getCategories } from '../features/category/categorySlice';
import { getAlicuotaPercent } from '../utils/tax';
import useSubscriptionRestrictions from '../hooks/useSubscriptionRestrictions';

const SalesReport = () => {
  const dispatch = useDispatch();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(''); // Nuevo filtro por categoría
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

  const { user } = useSelector(state => state.auth || {});
  const { tenant } = useSelector(state => state.tenant || {});
  const { categories } = useSelector(state => state.category || { categories: [] }); // Obtener categorías
  const { daysOverdue } = useSubscriptionRestrictions();
  const isSubscriptionExpired = daysOverdue > 0;
  const tenantId = user?.tenantId?._id || user?.tenantId || tenant?._id || tenant?.id;
  const navigate = useNavigate();

  // Estilos constantes para el reporte
  const primaryBtn = {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  };
  const secondaryBtn = {
    padding: '10px 20px',
    background: '#edf2f7',
    color: '#4a5568',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s'
  };
  const excelBtn = {
    padding: '10px 20px',
    background: '#2f855a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  };
  const dateInput = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e0',
    fontSize: '14px',
    outline: 'none',
    background: 'white',
    color: '#2d3748',
    width: '160px'
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = tenantId ? { tenantId, limit: 10000 } : { limit: 10000 };
        const resp = await httpRequest.get('/sales/all', { params });
        const salesArray = Array.isArray(resp.data.items) 
          ? resp.data.items 
          : (Array.isArray(resp.data.sales) ? resp.data.sales : (Array.isArray(resp.data) ? resp.data : []));
        setSales(salesArray);
        // Cargar categorías
        dispatch(getCategories());
      } catch (e) {
        console.error('Error cargando ventas:', e);
        toast.error('No se pudieron cargar las ventas');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId, dispatch]);

  const parseDateOnly = (d) => {
    if (!d) return null;
    const [year, month, day] = d.split('-');
    return new Date(year, month - 1, day);
  };
  const inRange = (sd) => {
    const dt = new Date(sd);
    const f = parseDateOnly(fromDate);
    const t = parseDateOnly(toDate);
    if (f && dt < new Date(f.getFullYear(), f.getMonth(), f.getDate(), 0, 0, 0)) return false;
    if (t && dt > new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999)) return false;
    return true;
  };

  const handleQuery = () => {
    setLoading(true);
    try {
      if (!Array.isArray(sales)) return;
      const filtered = sales.filter(s => {
        const sd = s.createdAt || s.date || s.timestamp;
        if (!sd) return false;

        // Filtro por fecha
        if (!inRange(sd)) return false;

        // Filtro por categoría (si hay una seleccionada)
        if (selectedCategory) {
          // Normalizar la categoría seleccionada para comparación
          const normalizedSelected = selectedCategory.trim().toLowerCase();

          // Verificar si algún item de la venta pertenece a la categoría seleccionada
          const hasCategory = (s.items || []).some(item => {
            // Intentar obtener la categoría de diferentes posibles campos
            const itemCategory = item.category || item.productCategory || '';
            const normalizedItem = String(itemCategory).trim().toLowerCase();
            return normalizedItem === normalizedSelected;
          });
          if (!hasCategory) return false;
        }

        return true;
      });
      setRows(filtered);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const sortedRows = (() => {
    const r = [...rows];
    const k = sort.key; const dir = sort.dir === 'asc' ? 1 : -1;
    r.sort((a, b) => {
      let va = a[k], vb = b[k];
      if (k === 'createdAt') { va = new Date(a.createdAt || a.date || 0).getTime(); vb = new Date(b.createdAt || b.date || 0).getTime(); }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return r;
  })();

  const totals = rows.reduce((acc, r) => {
    acc.count += 1;

    // Obtener configuración para calcular impuestos
    const isRI = r.fiscalStatus === 'Responsable Inscripto' || (Number(r.importeIVA || 0) > 0);

    // 1. Calcular el Revenue Neto Real de cada item considerando el descuento general de la venta
    // Si es RI, el 'price' guardado ya es neto. Si no es RI, es el total pagado.
    // Pero el 'saleDiscount' es siempre un monto final sacado del TOTAL.
    
    // Suma de subtotales de todos los items de la venta (tal como están guardados)
    const totalLinesSum = (r.items || []).reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
    // Margen de ajuste por descuento general: (Total Venta - IVA Venta) / Suma de Lineas
    // Si la venta es de $100 final, con $10 de descuento y 21% IVA. 
    // totalLinesSum (si es RI) = $82.64. r.total = $90. ratio = 90 / (82.64 * 1.21) = 0.9
    // Esto nos da cuánto del precio guardado realmente cobramos tras impuestos y descuentos.
    const saleGrossBeforeDiscount = (totalLinesSum + Number(r.importeIVA || 0));
    const saleRealTotal = Number(r.total || 0);
    const adjustmentFactor = saleGrossBeforeDiscount > 0 ? (saleRealTotal / saleGrossBeforeDiscount) : 1;

    (r.items || []).forEach(item => {
      // Filtrar por categoría si es necesario
      if (selectedCategory) {
        const itemCategory = String(item.category || '').trim().toLowerCase();
        if (itemCategory !== selectedCategory.trim().toLowerCase()) return;
      }

      const qty = Number(item.quantity || 0);
      const unitPrice = Number(item.price || 0); // Precio guardado (Neto si RI, Bruto si Mono)
      const unitCost = Number(item.costPrice || 0); // Costo guardado (Usualmente Bruto pagado a proveedor)

      // Revenue Real de la línea (considerando el factor cobrado vs total teórico)
      // Si es RI, queremos el NETO que nos queda. Si no es RI, queremos el BRUTO.
      let revenueLine;
      if (isRI) {
        // unitPrice ya es neto. Lo escalamos por el factor de cobro.
        revenueLine = qty * unitPrice * adjustmentFactor;
      } else {
        // unitPrice es bruto. Lo escalamos.
        revenueLine = qty * unitPrice * adjustmentFactor;
      }

      // Costo Real de la línea (Neto si somos RI para comparar peras con peras)
      let costLine;
      if (isRI) {
        // Asumimos que el costo guardado incluye el IVA que queremos recuperar (Fiscal Credit)
        // Usamos la alícuota del producto para netearlo.
        const ivaCode = item.alicuotaIVACode ?? 5;
        const ivaPct = getAlicuotaPercent(ivaCode) || 0;
        const netCost = unitCost / (1 + (ivaPct / 100));
        costLine = qty * netCost;
      } else {
        // Somos Monotributo, el costo es el total pagado (Gross)
        costLine = qty * unitCost;
      }

      const profitLine = revenueLine - costLine;

      acc.total += revenueLine;
      acc.cost += costLine;
      acc.profit += profitLine;
    });

    return acc;
  }, { count: 0, total: 0, cost: 0, profit: 0 });

  const downloadExcel = async () => {
    try {
      toast.info('Preparando descarga de Excel...');
      const params = {};
      if (fromDate) {
        const [y, m, d] = fromDate.split('-');
        params.startDate = new Date(y, m - 1, d, 0, 0, 0).toISOString();
      }
      if (toDate) {
        const [y, m, d] = toDate.split('-');
        params.endDate = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      }
      if (tenantId) params.tenantId = tenantId;
      const resp = await httpRequest.get('/reports/sales-export', { params, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const from = fromDate || 'all';
      const to = toDate || 'all';
      a.href = url; a.download = `reportes_ventas_${from}_to_${to}.xlsx`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
    } catch (e) {
      console.error('Error exportando:', e);
      toast.error('No se pudo generar el Excel');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        background: 'white',
        padding: 20,
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#1a202c' }}>🧾 Reporte de Ventas</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => navigate('/')} style={secondaryBtn}>🏠 Dashboard</button>
          <button onClick={() => navigate('/reports')} style={primaryBtn}>📊 Reportes</button>
        </div>
      </div>

      {/* Filtros mejorados */}
      <div style={{
        background: 'white',
        padding: 20,
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#4a5568' }}>Desde</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={dateInput}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#4a5568' }}>Hasta</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={dateInput}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#4a5568' }}>Categoría</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ ...dateInput, width: '180px' }}
            >
              <option value="">Todas las categorías</option>
              {(categories || []).map((cat) => (
                <option key={cat._id || cat.id} value={cat.category}>
                  {cat.category}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 'auto' }}>
            <button onClick={handleQuery} disabled={loading} style={primaryBtn}>
              {loading ? '⏳ Consultando...' : '🔍 Consultar'}
            </button>
            <button 
              onClick={isSubscriptionExpired ? () => toast.error('Suscripción vencida. Renueva para exportar.') : downloadExcel} 
              disabled={loading || isSubscriptionExpired} 
              style={{
                ...excelBtn,
                background: isSubscriptionExpired ? '#cbd5e0' : excelBtn.background,
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                opacity: isSubscriptionExpired ? 0.7 : 1
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Exportación deshabilitada" : "Exportar a Excel"}
            >
              📥 Exportar Excel
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        Resultados: {totals.count} ventas
        {selectedCategory && <span style={{ color: '#667eea', fontWeight: 'bold' }}> (Categoría: {selectedCategory})</span>} |
        Total: ${totals.total.toFixed(2)} |
        <span style={{ color: '#2c7a7b', fontWeight: 'bold' }}> Ganancia Total: ${totals.profit.toFixed(2)}</span>
      </div>

      {sortedRows.length === 0 ? (
        <p style={{ color: '#666' }}>No se encontraron ventas en el rango seleccionado.</p>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#007bff', color: '#fff' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold' }} onClick={() => toggleSort('createdAt')}>Fecha {sort.key === 'createdAt' ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold' }}>ID Venta</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold' }}>Cliente</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold' }}>Ítems</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }} onClick={() => toggleSort('total')}>Total {sort.key === 'total' ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>Costo</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>Utilidad</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((s) => {
                // Si hay categoría seleccionada, calcular solo para items de esa categoría
                let categoryItems = s.items || [];
                if (selectedCategory) {
                  const normalizedSelected = selectedCategory.trim().toLowerCase();
                  categoryItems = categoryItems.filter(item => {
                    const itemCategory = String(item.category || '').trim().toLowerCase();
                    return itemCategory === normalizedSelected;
                  });
                }

                // Obtener configuración para calcular impuestos
                const isRI = s.fiscalStatus === 'Responsable Inscripto' || (Number(s.importeIVA || 0) > 0);

                // 1. Calcular factor de ajuste (incluye descuentos generales y recargos)
                const totalLinesSum = (s.items || []).reduce((sum, it) => sum + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
                const saleGrossBeforeDiscount = (totalLinesSum + Number(s.importeIVA || 0));
                const saleRealTotal = Number(s.total || 0);
                const adjustmentFactor = saleGrossBeforeDiscount > 0 ? (saleRealTotal / saleGrossBeforeDiscount) : 1;

                // 2. Calcular totales reales filtrados por categoría
                let categoryTotal = 0;
                let categoryCost = 0;

                categoryItems.forEach(item => {
                  const qty = Number(item.quantity || 0);
                  const unitPrice = Number(item.price || 0);
                  const unitCost = Number(item.costPrice || 0);

                  // Revenue Real (Neto si RI, Bruto si Mono)
                  categoryTotal += qty * unitPrice * adjustmentFactor;

                  // Costo Real (Neto si RI, Bruto si Mono)
                  if (isRI) {
                    const ivaCode = item.alicuotaIVACode ?? 5;
                    const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                    categoryCost += qty * (unitCost / (1 + (ivaPct / 100)));
                  } else {
                    categoryCost += qty * unitCost;
                  }
                });

                const categoryProfit = categoryTotal - categoryCost;

                return (
                  <tr key={s._id || s.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>{new Date(s.createdAt || s.date).toLocaleString()}</td>
                    <td style={{ padding: 12 }}>{s._id || s.id}</td>
                    <td style={{ padding: 12 }}>{(s.customer && (s.customer.name || s.customer.fullName)) || s.clientName || s.client || '-'}</td>
                    <td style={{ padding: 12 }}>{categoryItems.length}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>${categoryTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{ padding: 12, textAlign: 'right', color: '#666' }}>${categoryCost.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>
                      <span style={{ color: categoryProfit >= 0 ? '#28a745' : '#dc3545' }}>
                        ${categoryProfit.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <br />
                      <span style={{ fontSize: '10px', color: '#666', fontWeight: 'normal' }}>
                        ({categoryTotal > 0 ? ((categoryProfit / categoryTotal) * 100).toFixed(1) : 0}%)
                      </span>
                    </td>
                    <td style={{ padding: 12 }}>{s.status || s.estado || 'OK'}</td>
                  </tr>
                );
              })}
              <tr style={{ background: '#fafafa', fontWeight: 'bold' }}>
                <td style={{ padding: 12 }} colSpan={4}>Totales</td>
                <td style={{ padding: 12, textAlign: 'right' }}>${totals.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style={{ padding: 12, textAlign: 'right', color: '#666' }}>${totals.cost.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style={{ padding: 12, textAlign: 'right', color: '#2c7a7b' }}>
                  ${totals.profit.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <br />
                  <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>
                    ({totals.total > 0 ? ((totals.profit / totals.total) * 100).toFixed(1) : 0}%)
                  </span>
                </td>
                <td style={{ padding: 12 }} />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Detalle de productos vendidos */}
      {sortedRows.length > 0 && (() => {
        // Calcular resumen de productos vendidos
        const productSummary = {};
        const normalizedSelected = selectedCategory ? selectedCategory.trim().toLowerCase() : null;

        sortedRows.forEach(sale => {
          (sale.items || []).forEach(item => {
            const itemCategory = String(item.category || '').trim().toLowerCase();
            if (!normalizedSelected || itemCategory === normalizedSelected) {
              const productName = item.name || 'Producto sin nombre';
              if (!productSummary[productName]) {
                productSummary[productName] = {
                  quantity: 0,
                  revenue: 0,
                  cost: 0
                };
              }
              productSummary[productName].quantity += Number(item.quantity || 0);

              // Para el detalle también aplicamos lógica RI si corresponde
              const isRI = sale.fiscalStatus === 'Responsable Inscripto' || (Number(sale.importeIVA || 0) > 0);
              const qty = Number(item.quantity || 0);
              const unitPrice = Number(item.price || 0);
              const unitCost = Number(item.costPrice || 0);

              // Ajuste por descuento general de la venta
              const tLineSum = (sale.items || []).reduce((sum, it) => sum + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
              const gBeforeDisc = (tLineSum + Number(sale.importeIVA || 0));
              const rTotal = Number(sale.total || 0);
              const aFactor = gBeforeDisc > 0 ? (rTotal / gBeforeDisc) : 1;

              const revenue = qty * unitPrice * aFactor;
              let cost = 0;
              if (isRI) {
                const ivaCode = item.alicuotaIVACode ?? 5;
                const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                cost = qty * (unitCost / (1 + (ivaPct / 100)));
              } else {
                cost = qty * unitCost;
              }

              productSummary[productName].revenue += revenue;
              productSummary[productName].cost += cost;
            }
          });
        });

        const productList = Object.entries(productSummary)
          .map(([name, data]) => ({
            name,
            quantity: data.quantity,
            revenue: data.revenue,
            profit: data.revenue - data.cost
          }))
          .sort((a, b) => b.revenue - a.revenue); // Ordenar por ingresos

        if (productList.length === 0) return null;

        return (
          <div style={{
            marginTop: 24,
            background: 'white',
            padding: 20,
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700, color: '#1a202c' }}>
              📦 Resumen Ventas por Producto {selectedCategory ? `- ${selectedCategory}` : ''}
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#667eea', color: '#fff' }}>
                    <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold' }}>Producto</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 'bold' }}>Cantidad</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>Total Venta</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>Costo</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>Utilidad</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>Margen %</th>
                  </tr>
                </thead>
                <tbody>
                  {productList.map((product, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 12 }}>{product.name}</td>
                      <td style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>{product.quantity}</td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        ${product.revenue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', color: '#666' }}>
                        ${(product.revenue - product.profit).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', color: product.profit >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                        ${product.profit.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', color: '#666', fontSize: '12px' }}>
                        {product.revenue > 0 ? ((product.profit / product.revenue) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f7fafc', fontWeight: 'bold' }}>
                    <td style={{ padding: 12 }}>TOTAL</td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      {productList.reduce((sum, p) => sum + p.quantity, 0)}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right' }}>
                      ${productList.reduce((sum, p) => sum + p.revenue, 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', color: '#666' }}>
                      ${(productList.reduce((sum, p) => sum + p.revenue, 0) - productList.reduce((sum, p) => sum + p.profit, 0)).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', color: '#2c7a7b' }}>
                      ${productList.reduce((sum, p) => sum + p.profit, 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', color: '#666', fontSize: '13px' }}>
                      {(() => {
                        const tRev = productList.reduce((sum, p) => sum + p.revenue, 0);
                        const tProf = productList.reduce((sum, p) => sum + p.profit, 0);
                        return tRev > 0 ? ((tProf / tRev) * 100).toFixed(1) : 0;
                      })()}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
};



export default SalesReport;

